import { useCallback, useEffect, useRef, useState } from 'react'
import { InsurancePlan } from '@dataTypes/insurance.types'
import { IPhases, TxPlanStatus } from '@dataTypes/treatmentPlan.types'
import { getEnv } from '@utils/env'
import {
  applyTxPlanVoiceOperations,
  createTxPlanVoiceState,
  TxPlanVoiceCursorState,
  TxPlanVoiceProcedure,
} from '@utils/tx-plan-voice-apply.utils'
import {
  formatTxPlanTranscript,
  parseTxPlanFinalTranscript,
} from '@utils/tx-plan-transcript.utils'
import {
  FinalTranscriptEvent,
  StreamingVoicePhase,
  useStreamingVoiceSession,
} from '@hooks/useStreamingVoiceSession'
import type { VoiceTranscriptEntry } from '@hooks/useVoiceTranscriptFeed'

const treatmentPlanBase = (): string => {
  const base = getEnv('REACT_APP_BUSINESS_LAYER_TREAT_PLAN').replace(/\/$/, '')
  if (!base)
    throw new Error('The treatment plan service URL is not configured.')
  return base
}

export type TxPlanVoiceChart = {
  patient_id: string
  clinic_id: string
  tabKey: string
  phases: IPhases[]
  activeAccordionKeys: string[]
}

export function useTxPlanStreamingVoice({
  enabled,
  contextKey,
  chart,
  procedures,
  insurance,
  discountPercent,
  isEditing,
  onForm,
  onPendingStatus,
  onSearch,
}: {
  enabled: boolean
  contextKey: string
  chart: TxPlanVoiceChart
  procedures: TxPlanVoiceProcedure[]
  insurance: InsurancePlan | null
  discountPercent: number
  isEditing: boolean
  onForm: (next: {
    phases: IPhases[]
    activeAccordionKeys: string[]
    phasesChanged: boolean
    closeProcedureModal?: boolean
  }) => void
  onPendingStatus: (pending: {
    phaseIndex: number
    status: TxPlanStatus
  }) => void
  onSearch: (pending: { phaseIndex: number; query: string }) => void
}): {
  phase: StreamingVoicePhase
  message: string
  toggle: () => void
  stop: () => void
  keyword: TxPlanVoiceCursorState
  backlog: number
  recognized: string
  liveText: string
  history: VoiceTranscriptEntry[]
  queued: string[]
} {
  const [keyword, setKeyword] = useState<TxPlanVoiceCursorState>(() =>
    createTxPlanVoiceState(chart.phases),
  )
  const keywordRef = useRef(keyword)
  const updateKeyword = useCallback((next: TxPlanVoiceCursorState): void => {
    keywordRef.current = next
    setKeyword(next)
  }, [])
  const contextRef = useRef({
    procedures,
    insurance,
    discountPercent,
    isEditing,
  })
  contextRef.current = { procedures, insurance, discountPercent, isEditing }
  const onFormRef = useRef(onForm)
  onFormRef.current = onForm
  const onPendingStatusRef = useRef(onPendingStatus)
  onPendingStatusRef.current = onPendingStatus
  const onSearchRef = useRef(onSearch)
  onSearchRef.current = onSearch

  const onFinalTranscript = useCallback(
    (event: FinalTranscriptEvent<TxPlanVoiceChart>): 'continue' | 'stop' => {
      try {
        if (event.failed)
          throw new Error('Transcription failed. Repeat this phrase.')
        if (!event.text.trim()) {
          event.setMessage('Say a treatment plan command.', 'info')
          return 'continue'
        }
        const operations = parseTxPlanFinalTranscript({
          text: event.text,
          normalized: event.normalized,
        })
        const result = applyTxPlanVoiceOperations(
          event.chart.phases,
          event.chart.activeAccordionKeys,
          keywordRef.current,
          operations,
          contextRef.current,
        )
        event.syncChart({
          ...event.chart,
          phases: result.phases,
          activeAccordionKeys: result.activeAccordionKeys,
        })
        if (keywordRef.current.paused !== result.state.paused) {
          event.setPaused(result.state.paused)
        }
        updateKeyword(result.state)
        const accordionChanged =
          result.activeAccordionKeys.join('|') !==
          event.chart.activeAccordionKeys.join('|')
        // Close Add Procedure when voice takes over a partial code (tooth/surfaces follow-up).
        const closeProcedureModal = Boolean(result.state.pendingAdd)
        if (result.phasesChanged || accordionChanged || closeProcedureModal) {
          onFormRef.current({
            phases: result.phases,
            activeAccordionKeys: result.activeAccordionKeys,
            phasesChanged: result.phasesChanged,
            ...(closeProcedureModal ? { closeProcedureModal: true } : {}),
          })
        }
        if (result.pendingStatus) {
          onPendingStatusRef.current(result.pendingStatus)
        }
        if (result.pendingSearch) {
          onSearchRef.current(result.pendingSearch)
        }
        event.setMessage(result.message, result.rejected ? 'failed' : 'applied')
        if (result.rejected) event.discardQueued()
        return result.control === 'stop' ? 'stop' : 'continue'
      } catch (error) {
        const reason =
          error instanceof Error ? error.message : 'Phrase rejected.'
        event.discardQueued()
        event.setMessage(
          `${reason} Queued phrases were discarded. ${
            keywordRef.current.paused
              ? 'Entry remains paused. Say “resume” when ready.'
              : 'Still listening.'
          }`,
          'failed',
        )
        return 'continue'
      }
    },
    [updateKeyword],
  )

  const {
    phase,
    message,
    toggle,
    stop,
    backlog,
    recognized,
    liveText,
    history,
    queued,
  } = useStreamingVoiceSession({
    enabled,
    contextKey,
    chart,
    resolveBaseUrl: treatmentPlanBase,
    socketPath: '/api/v1/treatplans/socket.io/',
    eventPrefix: 'treatment_plan_voice',
    formatTranscript: formatTxPlanTranscript,
    listeningMessage: () =>
      'Listening for treatment plan commands. Pause briefly after each phrase.',
    onStartReset: () => {
      updateKeyword(createTxPlanVoiceState(chart.phases))
    },
    onStopReset: () => {
      const next = {
        ...keywordRef.current,
        paused: false,
        pendingAdd: undefined,
      }
      keywordRef.current = next
      setKeyword(next)
    },
    onFinalTranscript,
  })

  useEffect(() => {
    updateKeyword(createTxPlanVoiceState())
  }, [contextKey, updateKeyword])

  return {
    phase,
    message,
    toggle,
    stop,
    keyword,
    backlog,
    recognized,
    liveText,
    history,
    queued,
  }
}
